import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useRegion } from '../context/RegionContext';
import { getEnabledRegions } from '../data/regions';

const BASE_URL = 'https://ishopgenie.co';

/**
 * Enhanced SEO Component for managing meta tags, hreflang, and structured data
 * Supports: BlogPosting, BreadcrumbList, Organization, WebSite, FAQPage schemas
 * 
 * @param {string} title - Page title
 * @param {string} description - Meta description
 * @param {string} type - Page type (website, article)
 * @param {object} article - Article data for BlogPosting schema
 * @param {array} breadcrumbs - Breadcrumb items for BreadcrumbList schema
 * @param {string} canonicalPath - Canonical URL path
 * @param {string} image - OG image URL
 * @param {boolean} noIndex - Whether to noindex the page
 * @param {array} faqs - FAQ items for FAQPage schema
 * @param {string} pageType - Type for pSEO pages (brand, category, event)
 */
export const SEO = ({ 
  title,
  description,
  type = 'website',
  article = null,
  breadcrumbs = [],
  canonicalPath = '',
  image = null,
  noIndex = false,
  faqs = [],
  pageType = null
}) => {
  const { currentRegion, region } = useRegion();
  const enabledRegions = getEnabledRegions();
  
  // Build canonical URL
  const currentPath = canonicalPath || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const canonicalUrl = `${BASE_URL}${currentPath}`;
  
  // Get path without region prefix for hreflang generation
  const pathWithoutRegion = currentPath.replace(/^\/(my|sg|ph|id)/, '') || '/';
  
  // Generate hreflang tags for all enabled regions
  const generateHreflangTags = () => {
    const tags = [];
    
    enabledRegions.forEach(reg => {
      const url = reg.code === 'global' 
        ? `${BASE_URL}${pathWithoutRegion}` 
        : `${BASE_URL}/${reg.code}${pathWithoutRegion}`;
      
      // Generate proper hreflang code
      let hreflang;
      if (reg.code === 'global') {
        hreflang = 'x-default';
      } else {
        // Map region codes to proper hreflang format
        const regionMap = {
          'my': 'en-MY',
          'sg': 'en-SG',
          'ph': 'en-PH',
          'id': 'id-ID'
        };
        hreflang = regionMap[reg.code] || `en-${reg.code.toUpperCase()}`;
      }
      
      tags.push({
        rel: 'alternate',
        hrefLang: hreflang,
        href: url
      });
    });
    
    // Add English fallback
    tags.push({
      rel: 'alternate',
      hrefLang: 'en',
      href: `${BASE_URL}${pathWithoutRegion}`
    });
    
    return tags;
  };
  
  // Organization Schema (for website-wide)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}/#organization`,
    name: 'iShopGenie',
    alternateName: 'iShop Genie',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logo.png`,
      width: 512,
      height: 512
    },
    description: 'Your trusted source for the latest deals, exclusive coupons, and shopping guides. Save money on everything you buy.',
    sameAs: [
      'https://facebook.com/ishopgenie',
      'https://twitter.com/ishopgenie',
      'https://instagram.com/ishopgenie'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'hello@ishopgenie.co',
      contactType: 'customer service',
      availableLanguage: ['English']
    },
    areaServed: [
      { '@type': 'Country', name: 'Malaysia' },
      { '@type': 'Country', name: 'Singapore' },
      { '@type': 'Country', name: 'Philippines' },
      { '@type': 'Country', name: 'Indonesia' }
    ]
  };
  
  // Website Schema with SearchAction
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    name: 'iShopGenie',
    alternateName: 'iShop Genie - Deals & Coupons',
    url: BASE_URL,
    publisher: {
      '@id': `${BASE_URL}/#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    },
    inLanguage: 'en'
  };
  
  // BlogPosting Schema (for articles)
  const generateArticleSchema = () => {
    if (!article) return null;
    
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      '@id': `${canonicalUrl}#article`,
      headline: article.title,
      description: article.excerpt || description,
      image: article.featured_image || image ? {
        '@type': 'ImageObject',
        url: article.featured_image || image,
        width: 1200,
        height: 630
      } : undefined,
      author: {
        '@type': 'Person',
        name: article.author_name,
        url: `${BASE_URL}/about`
      },
      publisher: {
        '@id': `${BASE_URL}/#organization`
      },
      datePublished: article.published_at || article.created_at,
      dateModified: article.updated_at || article.created_at,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl
      },
      articleSection: article.category_name,
      keywords: article.tags ? article.tags.join(', ') : '',
      wordCount: article.content ? article.content.split(/\s+/).length : 0,
      timeRequired: `PT${article.reading_time || 5}M`,
      isPartOf: {
        '@id': `${BASE_URL}/#website`
      },
      inLanguage: 'en'
    };
  };
  
  // BreadcrumbList Schema
  const generateBreadcrumbSchema = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;
    
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.url ? `${BASE_URL}${crumb.url}` : undefined
      }))
    };
  };
  
  // FAQPage Schema
  const generateFAQSchema = () => {
    if (!faqs || faqs.length === 0) return null;
    
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question || faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer || faq.a
        }
      }))
    };
  };
  
  // WebPage Schema for pSEO pages
  const generateWebPageSchema = () => {
    if (!pageType) return null;
    
    const pageTypeMap = {
      'brand': 'CollectionPage',
      'category': 'CollectionPage',
      'event': 'WebPage'
    };
    
    return {
      '@context': 'https://schema.org',
      '@type': pageTypeMap[pageType] || 'WebPage',
      '@id': canonicalUrl,
      name: title,
      description: description,
      url: canonicalUrl,
      isPartOf: {
        '@id': `${BASE_URL}/#website`
      },
      breadcrumb: breadcrumbs?.length > 0 ? {
        '@id': `${canonicalUrl}#breadcrumb`
      } : undefined,
      inLanguage: 'en'
    };
  };
  
  const hreflangTags = generateHreflangTags();
  const articleSchema = generateArticleSchema();
  const breadcrumbSchema = generateBreadcrumbSchema();
  const faqSchema = generateFAQSchema();
  const webPageSchema = generateWebPageSchema();
  
  // Build full title
  const fullTitle = title ? `${title} | iShopGenie` : 'iShopGenie - Best Deals, Coupons & Shopping Guides';
  
  // Default description
  const metaDescription = description || 'Discover the best deals, exclusive coupons, and expert shopping guides. Save money on electronics, fashion, travel, and more.';
  
  // Default OG image
  const ogImage = image || `${BASE_URL}/og-image.png`;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content="iShopGenie" />
      <meta name="publisher" content="iShopGenie" />
      {article?.tags && <meta name="keywords" content={article.tags.join(', ')} />}
      
      {/* Canonical & Hreflang */}
      <link rel="canonical" href={canonicalUrl} />
      {hreflangTags.map((tag, index) => (
        <link key={index} rel={tag.rel} hrefLang={tag.hrefLang} href={tag.href} />
      ))}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'article' ? 'article' : 'website'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="iShopGenie" />
      <meta property="og:locale" content={currentRegion === 'id' ? 'id_ID' : 'en_US'} />
      {currentRegion !== 'global' && (
        <meta property="og:locale:alternate" content={currentRegion === 'my' ? 'en_MY' : currentRegion === 'sg' ? 'en_SG' : currentRegion === 'ph' ? 'en_PH' : 'en_US'} />
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ishopgenie" />
      <meta name="twitter:creator" content="@ishopgenie" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Article specific meta */}
      {article && (
        <>
          <meta property="article:published_time" content={article.published_at || article.created_at} />
          <meta property="article:modified_time" content={article.updated_at} />
          <meta property="article:author" content={article.author_name} />
          <meta property="article:section" content={article.category_name} />
          {article.tags && article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      {webPageSchema && (
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
